/**
 * ShuddhiDoot Purification Module Container
 * SAUB-FE-003: Complete Purification Enhancement with Modern UI
 */

import React, { Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import BaseLayout from '@/components/layout/BaseLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  BarChart3,
  RotateCcw,
  CheckCircle,
  Wrench,
  Package,
  Droplets,
  Activity,
  Gauge,
  Factory,
  AlertTriangle,
  TrendingUp,
  Users,
  Settings
} from 'lucide-react';

// Lazy loaded page components for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));

const CycleManagement = React.lazy(() =>
  import('./pages/CycleManagement/CycleManagement').then(module => ({
    default: module.CycleManagement
  }))
);

const QualityControl = React.lazy(() =>
  import('./pages/QualityControl/QualityControl').then(module => ({
    default: module.QualityControl
  }))
);

const MaintenanceManagement = React.lazy(() =>
  import('./pages/MaintenanceManagement/MaintenanceManagement').then(module => ({
    default: module.MaintenanceManagement
  }))
);

// Disabled: Inventory Transfer (commented out)
// const InventoryTransfer = React.lazy(() =>
//   import('./pages/InventoryTransfer/InventoryTransfer').then(module => ({
//     default: module.InventoryTransferPage
//   }))
// );

// Disabled: Slurry Management (commented out)
// const SlurryManagement = React.lazy(() =>
//   import('./pages/SlurryManagement/SlurryManagement').then(module => ({
//     default: module.SlurryManagement
//   }))
// );

const RealTimeMonitoring = React.lazy(() =>
  import('./pages/RealTimeMonitoring/RealTimeMonitoring').then(module => ({
    default: module.RealTimeMonitoring
  }))
);

// Batch Management pages
const BatchListPage = React.lazy(() => import('../../pages/batch/BatchListPage'));
const BatchCreatePage = React.lazy(() => import('../../pages/batch/BatchCreatePage'));
const BatchDetailPage = React.lazy(() => import('../../pages/batch/BatchDetailPage'));
const BatchEditPage = React.lazy(() => import('../../pages/batch/BatchEditPage'));
const BatchStatisticsPage = React.lazy(() => import('../../pages/batch/BatchStatisticsPage'));

// Cycle Management pages
const CycleListPage = React.lazy(() => import('../../pages/cycle/CycleListPage'));
const CycleCreatePage = React.lazy(() => import('../../pages/cycle/CycleCreatePage'));
const CycleDetailPage = React.lazy(() => import('../../pages/cycle/CycleDetailPage'));
const CycleEditPage = React.lazy(() => import('../../pages/cycle/CycleEditPage'));
const CycleComparisonPage = React.lazy(() => import('../../pages/cycle/CycleComparisonPage'));

// Alert Management pages
const AlertsListPage = React.lazy(() => import('../../pages/purification/alerts/AlertsListPage'));
const AlertDetailsPage = React.lazy(() => import('../../pages/purification/alerts/AlertDetailsPage'));
const ConfigurationsPage = React.lazy(() => import('../../pages/purification/alerts/ConfigurationsPage'));
const StatisticsPage = React.lazy(() => import('../../pages/purification/alerts/StatisticsPage'));

// Maintenance Management pages
const SchedulesListPage = React.lazy(() => import('../../pages/maintenance/SchedulesListPage'));
const ScheduleDetailPage = React.lazy(() => import('../../pages/maintenance/ScheduleDetailPage'));
const ScheduleFormPage = React.lazy(() => import('../../pages/maintenance/ScheduleFormPage'));
const ReportDetailPage = React.lazy(() => import('../../pages/maintenance/ReportDetailPage'));
const ReportFormPage = React.lazy(() => import('../../pages/maintenance/ReportFormPage'));
const TemplatesListPage = React.lazy(() => import('../../pages/maintenance/TemplatesListPage'));

// Enhanced loading component with modern design
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Card className="w-full max-w-md">
      <CardContent className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <CardTitle className="text-lg font-medium text-gray-600 mb-2">
          Loading ShuddhiDoot Module...
        </CardTitle>
        <CardDescription className="text-center text-gray-500">
          Initializing purification systems and real-time data feeds
        </CardDescription>
      </CardContent>
    </Card>
  </div>
);

// Quick metrics display for overview
const QuickMetrics: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">CH₄ Purity</p>
            <p className="text-2xl font-bold text-green-600">96.5%</p>
          </div>
          <Gauge className="h-8 w-8 text-green-600" />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Active Cycles</p>
            <p className="text-2xl font-bold text-blue-600">3</p>
          </div>
          <RotateCcw className="h-8 w-8 text-blue-600" />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">System Status</p>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Operational
            </Badge>
          </div>
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Efficiency</p>
            <p className="text-2xl font-bold text-purple-600">94.2%</p>
          </div>
          <TrendingUp className="h-8 w-8 text-purple-600" />
        </div>
      </CardContent>
    </Card>
  </div>
);

const PurificationModule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = React.useState<import('@/components/layout/BaseLayout').BreadcrumbItem[]>([]);

  // Base path for the purification module
  const basePath = '/purification';

  // Update breadcrumbs based on current location
  React.useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const newBreadcrumbs: import('@/components/layout/BaseLayout').BreadcrumbItem[] = [
      { label: 'Purification', onClick: () => navigate(basePath) }
    ];

    if (pathSegments.length > 1) {
      // Add page-specific breadcrumbs
      if (pathSegments[1] === 'batches') {
        newBreadcrumbs.push({ label: 'Batch Management' });
        if (pathSegments[2] && pathSegments[2] !== 'create' && pathSegments[2] !== 'statistics') {
          newBreadcrumbs.push({ label: `Batch ${pathSegments[2]}` });
        } else if (pathSegments[2] === 'create') {
          newBreadcrumbs.push({ label: 'Create Batch' });
        } else if (pathSegments[2] === 'statistics') {
          newBreadcrumbs.push({ label: 'Statistics' });
        }
      } else if (pathSegments[1] === 'cycles') {
        newBreadcrumbs.push({ label: 'Cycle Management' });
        if (pathSegments[2] && pathSegments[2] !== 'compare' && pathSegments[2] !== 'create') {
          if (pathSegments[3] === 'edit') {
            newBreadcrumbs.push({ label: `Cycle ${pathSegments[2]}` });
            newBreadcrumbs.push({ label: 'Edit' });
          } else {
            newBreadcrumbs.push({ label: `Cycle ${pathSegments[2]}` });
          }
        } else if (pathSegments[2] === 'compare') {
          newBreadcrumbs.push({ label: 'Compare Cycles' });
        } else if (pathSegments[2] === 'create') {
          newBreadcrumbs.push({ label: 'Create Cycle' });
        }
      } else if (pathSegments[1] === 'alerts') {
        newBreadcrumbs.push({ label: 'Alerts & Monitoring' });
        if (pathSegments[2] && pathSegments[2] !== 'configurations' && pathSegments[2] !== 'statistics') {
          newBreadcrumbs.push({ label: `Alert ${pathSegments[2]}` });
        } else if (pathSegments[2] === 'configurations') {
          newBreadcrumbs.push({ label: 'Configurations' });
        } else if (pathSegments[2] === 'statistics') {
          newBreadcrumbs.push({ label: 'Statistics' });
        }
      } else if (pathSegments[1] === 'quality') {
        newBreadcrumbs.push({ label: 'Quality Control' });
      } else if (pathSegments[1] === 'maintenance') {
        newBreadcrumbs.push({ label: 'Maintenance' });
        if (pathSegments[2]) {
          newBreadcrumbs.push({ label: pathSegments[2].replace('-', ' ').toUpperCase() });
        }
      // } else if (pathSegments[1] === 'inventory') {
      //   newBreadcrumbs.push({ label: 'Inventory Transfer' });
      // } else if (pathSegments[1] === 'slurry') {
      //   newBreadcrumbs.push({ label: 'Slurry Management' });
      } else if (pathSegments[1] === 'monitoring') {
        newBreadcrumbs.push({ label: 'Real-time Monitoring' });
      }
    }

    setBreadcrumbs(newBreadcrumbs);
  }, [location.pathname, navigate, basePath]);

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}`),
      isActive: location.pathname === basePath || location.pathname === `${basePath}/`,
      badge: undefined
    },
    {
      id: 'batches',
      label: 'Batch Management',
      icon: <Package className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/batches`),
      isActive: location.pathname.includes(`${basePath}/batches`),
      badge: undefined
    },
    {
      id: 'cycles',
      label: 'Cycle Management',
      icon: <RotateCcw className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/cycles`),
      isActive: location.pathname === `${basePath}/cycles`,
      badge: '3' // Active cycles
    },
    {
      id: 'alerts',
      label: 'Alerts & Monitoring',
      icon: <AlertTriangle className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/alerts`),
      isActive: location.pathname.includes(`${basePath}/alerts`),
      badge: undefined
    },
    {
      id: 'quality',
      label: 'Quality Control',
      icon: <CheckCircle className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/quality`),
      isActive: location.pathname === `${basePath}/quality`,
      badge: undefined
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: <Wrench className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/maintenance`),
      isActive: location.pathname === `${basePath}/maintenance`,
      badge: '2' // Pending maintenance
    },
    // Disabled: Inventory Transfer
    // {
    //   id: 'inventory',
    //   label: 'Inventory Transfer',
    //   icon: <Factory className="w-5 h-5" />,
    //   onClick: () => navigate(`${basePath}/inventory`),
    //   isActive: location.pathname === `${basePath}/inventory`,
    //   badge: '5' // Pending transfers
    // },
    // Disabled: Slurry Management
    // {
    //   id: 'slurry',
    //   label: 'Slurry Management',
    //   icon: <Droplets className="w-5 h-5" />,
    //   onClick: () => navigate(`${basePath}/slurry`),
    //   isActive: location.pathname === `${basePath}/slurry`,
    //   badge: undefined
    // },
    {
      id: 'monitoring',
      label: 'Real-time Monitoring',
      icon: <Activity className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/monitoring`),
      isActive: location.pathname === `${basePath}/monitoring`,
      badge: undefined
    }
  ];

  return (
    <BaseLayout
      moduleName="ShuddhiDoot"
      moduleSubtitle="Biogas Purification & Quality Management"
      navigationItems={navigationItems}
      userInfo={{
        name: "Purification Manager",
        role: "Plant Operations Head"
      }}
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-6">
        {/* Main content area */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Default route redirects to dashboard */}
            <Route path="/" element={<Dashboard />} />

            {/* Batch Management routes */}
            <Route path="/batches" element={<BatchListPage />} />
            <Route path="/batches/create" element={<BatchCreatePage />} />
            <Route path="/batches/statistics" element={<BatchStatisticsPage />} />
            <Route path="/batches/:id/edit" element={<BatchEditPage />} />
            <Route path="/batches/:id" element={<BatchDetailPage />} />

            {/* Cycle Management routes */}
            <Route path="/cycles" element={<CycleListPage />} />
            <Route path="/cycles/create" element={<CycleCreatePage />} />
            <Route path="/cycles/compare" element={<CycleComparisonPage />} />
            <Route path="/cycles/:id/edit" element={<CycleEditPage />} />
            <Route path="/cycles/:id" element={<CycleDetailPage />} />

            {/* Alert Management routes */}
            <Route path="/alerts" element={<AlertsListPage />} />
            <Route path="/alerts/:id" element={<AlertDetailsPage />} />
            <Route path="/alerts/configurations" element={<ConfigurationsPage />} />
            <Route path="/alerts/statistics" element={<StatisticsPage />} />

            {/* Other purification module routes */}
            <Route path="/cycles-old" element={<CycleManagement />} />
            <Route path="/quality" element={<QualityControl />} />
            {/* Disabled: Inventory Transfer */}
            {/* <Route path="/inventory" element={<InventoryTransfer />} /> */}
            {/* Disabled: Slurry Management */}
            {/* <Route path="/slurry" element={<SlurryManagement />} /> */}
            <Route path="/monitoring" element={<RealTimeMonitoring />} />

            {/* Maintenance Management routes */}
            <Route path="/maintenance" element={<MaintenanceManagement />} />
            <Route path="/maintenance/schedules" element={<SchedulesListPage />} />
            <Route path="/maintenance/schedules/create" element={<ScheduleFormPage />} />
            <Route path="/maintenance/schedules/:id" element={<ScheduleDetailPage />} />
            <Route path="/maintenance/schedules/:id/edit" element={<ScheduleFormPage />} />
            <Route path="/maintenance/templates" element={<TemplatesListPage />} />
            <Route path="/maintenance/reports/create" element={<ReportFormPage />} />
            <Route path="/maintenance/reports/:id" element={<ReportDetailPage />} />
            <Route path="/maintenance/reports/:id/edit" element={<ReportFormPage />} />

            {/* Fallback route */}
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </Suspense>
      </div>
    </BaseLayout>
  );
};

export default PurificationModule;